import { useIxcSweepsV1SweepPlanQueryServiceSweepPlanGet } from '@interxfi/api-client/generated/hooks/ixc-sweeps-v1-sweep-plan-query-service-sweep-plan-get';
import {
  getReadonlyListIdList,
  getReadonlyListItemCurrency,
  getReadonlyListItemDate,
  getReadonlyListItemId,
  getReadonlyListItemNode,
  getReadonlyListItemNumber,
  getReadonlyListItemString,
  getReadonlyListItemTranslatedString,
  getReadonlyListItemYesOrNo,
} from '@interxfi/app-components/components/readonly-list/util';
import type { ReadonlyListItemRecord } from '@interxfi/design-system/components/list/readonly/item/readonly-list-item';
import { Skeleton } from '@interxfi/design-system/components/loading/skeleton';
import type {
  IxcPortfolioV1AccountConfig,
  IxcPortfolioV1BooksControls,
  IxcPortfolioV1BooksControlsSettings,
  IxcPortfolioV1DividendPaymentTransaction,
  IxcPortfolioV1FullPositionRule,
  IxcPortfolioV1HoldReduction,
  IxcPortfolioV1InstantDepositControls,
  IxcPortfolioV1InstantDepositControlsLimits,
  IxcPortfolioV1InstantDepositControlsSettings,
  IxcPortfolioV1ReverseLink,
  IxcPortfolioV1SweepsControls,
  IxcPortfolioV1SweepsControlsSettings,
} from '@interxfi/types/generated/api';
import { getNumericStringOrUndefinedFromNullable } from '@interxfi/utilities/object/access';
import { getAssetDetailPath } from '../asset/asset-routing';
import { getRegistrationAccountDetailPath } from '../registration/account/registration-account-routing';
import { getPortfolioHoldDetailPath } from './hold/portfolio-hold-routing';
import { getPortfolioTransactionDetailPath } from './transaction/portfolio-transaction-routing';

export function getPortfolioFullPositionRuleListItems(data: IxcPortfolioV1FullPositionRule | undefined, prefix = '') {
  return {
    threshold: getReadonlyListItemNumber(`${prefix}threshold`, 'Threshold', getNumericStringOrUndefinedFromNullable(data, 'threshold')),
    measureQuantity: getReadonlyListItemNumber(
      `${prefix}measureQuantity`,
      'Measure Quantity',
      getNumericStringOrUndefinedFromNullable(data, 'measureQuantity'),
    ),
  } as const satisfies ReadonlyListItemRecord;
}

function SweepPlanNameNode({ sweepPlanId }: { sweepPlanId: string }) {
  const { data, isPending } = useIxcSweepsV1SweepPlanQueryServiceSweepPlanGet({ sweepPlanId });
  if (isPending) {
    return <Skeleton charCount={10} />;
  }

  return data?.sweepPlan?.data?.name || sweepPlanId;
}

export function getPortfolioSweepsControlsSettingsListItems(data: IxcPortfolioV1SweepsControlsSettings | undefined, prefix = '') {
  return {
    canSweepAccounts: getReadonlyListItemYesOrNo(`${prefix}canSweepAccounts`, 'Allow Account Sweeps', Boolean(data?.canSweepAccounts)),
    defaultSweepPlanId: getReadonlyListItemId(`${prefix}defaultSweepPlanId`, 'Default Sweep Plan ID', data?.defaultSweepPlanId),
    defaultSweepPlanName: getReadonlyListItemNode(
      `${prefix}defaultSweepPlanName`,
      'Default Sweep Plan',
      data?.defaultSweepPlanId ? <SweepPlanNameNode sweepPlanId={data.defaultSweepPlanId} /> : undefined,
    ),
    eligibleSweepPlanIds: getReadonlyListIdList(`${prefix}eligibleSweepPlanIds`, 'Eligible Sweep Plan IDs', data?.eligibleSweepPlanIds),
  } as const satisfies ReadonlyListItemRecord;
}

export function getPortfolioSweepsControlsListItems(data: IxcPortfolioV1SweepsControls | undefined, prefix = '') {
  return {
    settings: getPortfolioSweepsControlsSettingsListItems(data?.settings, `${prefix}settings.`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getPortfolioBooksControlsSettingsListItems(data: IxcPortfolioV1BooksControlsSettings | undefined, prefix = '') {
  return {
    canCreditCustomers: getReadonlyListItemYesOrNo(`${prefix}canCreditCustomers`, 'Credit Customers', Boolean(data?.canCreditCustomers)),
    canDebitCustomers: getReadonlyListItemYesOrNo(`${prefix}canDebitCustomers`, 'Debit Customers', Boolean(data?.canDebitCustomers)),
    canTradeUnsettledCash: getReadonlyListItemYesOrNo(`${prefix}canTradeUnsettledCash`, 'Trade Unsettled Cash', Boolean(data?.canTradeUnsettledCash)),
  } as const satisfies ReadonlyListItemRecord;
}

export function getPortfolioBooksControlsListItems(data: IxcPortfolioV1BooksControls | undefined, prefix = '') {
  return {
    settings: getPortfolioBooksControlsSettingsListItems(data?.settings, `${prefix}settings.`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getPortfolioInstantDepositControlsSettingsListItems(data: IxcPortfolioV1InstantDepositControlsSettings | undefined, prefix = '') {
  return {
    canUseAdvance: getReadonlyListItemYesOrNo(`${prefix}canUseAdvance`, 'Allow Advances', Boolean(data?.canUseAdvance)),
  } as const satisfies ReadonlyListItemRecord;
}

export function getPortfolioInstantDepositControlsLimitsListItems(data: IxcPortfolioV1InstantDepositControlsLimits | undefined, prefix = '') {
  return {
    maxAdvancePerAccount: getReadonlyListItemCurrency(`${prefix}maxAdvancePerAccount`, 'Max Advance per Account', data?.maxAdvancePerAccount),
  } as const satisfies ReadonlyListItemRecord;
}

export function getPortfolioInstantDepositControlsListItems(data: IxcPortfolioV1InstantDepositControls | undefined, prefix = '') {
  return {
    settings: getPortfolioInstantDepositControlsSettingsListItems(data?.settings, `${prefix}settings.`),
    limits: getPortfolioInstantDepositControlsLimitsListItems(data?.limits, `${prefix}limits.`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getPortfolioAccountConfigListItems(data: IxcPortfolioV1AccountConfig | undefined, prefix = '') {
  return {
    marginType: getReadonlyListItemTranslatedString(`${prefix}marginType`, 'Margin Type', {
      namespace: 'ixcPortfolioV1',
      enumName: 'IxcPortfolioV1MarginType',
      value: data?.marginType,
    }),
    skipCreditCheck: getReadonlyListItemYesOrNo(`${prefix}skipCreditCheck`, 'Skip Credit Check', Boolean(data?.skipCreditCheck)),
  } as const satisfies ReadonlyListItemRecord;
}

export function getPortfolioReverseLinkListItems(data: IxcPortfolioV1ReverseLink | undefined, accountId: string | undefined, prefix = '') {
  return {
    transactionId: data?.transactionId
      ? getReadonlyListItemId(
          `${prefix}transactionId`,
          'Transaction ID',
          data.transactionId,
          getPortfolioTransactionDetailPath(accountId, data.transactionId),
        )
      : undefined,
    reason: data?.reason ? getReadonlyListItemString(`${prefix}reason`, 'Reason', data?.reason) : undefined,
  } as const satisfies ReadonlyListItemRecord;
}

export function getPortfolioHoldReductionListItems(data: IxcPortfolioV1HoldReduction | undefined, accountId: string | undefined, prefix = '') {
  return {
    holdId: getReadonlyListItemId(`${prefix}holdId`, 'Hold ID', data?.holdId, getPortfolioHoldDetailPath(accountId, data?.holdId)),
    reduceQuantity: getReadonlyListItemNumber(`${prefix}reduceQuantity`, 'Reduce Quantity', data?.reduceQuantity),
    updateMultiplier: data?.updateMultiplier
      ? getReadonlyListItemNumber(`${prefix}updateMultiplier`, 'Update Multiplier', data.updateMultiplier)
      : undefined,
  } as const satisfies ReadonlyListItemRecord;
}

export function getPortfolioDividendPaymentTransactionListItems(data: IxcPortfolioV1DividendPaymentTransaction | undefined, prefix = '') {
  return {
    dividendPaymentId: getReadonlyListItemId(`${prefix}dividendPaymentId`, 'Dividend Payment ID', data?.dividendPaymentId),
    accountId: getReadonlyListItemId(`${prefix}value.accountId`, 'Account ID', data?.accountId, getRegistrationAccountDetailPath(data?.accountId)),
    amount: getReadonlyListItemCurrency(`${prefix}amount`, 'Amount', data?.amount),
    category: getReadonlyListItemTranslatedString(`${prefix}category`, 'Category', {
      namespace: 'ixcPortfolioV1',
      enumName: 'IxcPortfolioV1DividendPaymentCategory',
      value: data?.category,
    }),
    description: getReadonlyListItemString(`${prefix}description`, 'Description', data?.description),
    assetId: getReadonlyListItemId(`${prefix}assetId`, 'Asset ID', data?.assetId, getAssetDetailPath(data?.assetId)),
    recordDate: getReadonlyListItemDate(`${prefix}recordDate`, 'Record Date', data?.recordDate, 'date_iso'),
    payDate: getReadonlyListItemDate(`${prefix}payDate`, 'Pay Date', data?.payDate, 'date_iso'),
  } as const satisfies ReadonlyListItemRecord;
}
